import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import localforage from 'localforage';
import { JS0N } from './ui/LoadingIndicator';
import { getMessaging, getToken } from "firebase/messaging";
import { initializeApp } from "firebase/app";
import { getFcmServiceWorkerRegistration } from '../fcmServiceWorker';
import { API_BASE_URL } from '../config/runtime';

const firebaseConfig = {
  apiKey: "AIzaSyDykKIhiu7KEiuT7ohS0EdP0RPAE2cBHeA",
  authDomain: "salew-app.firebaseapp.com",
  projectId: "salew-app",
  storageBucket: "salew-app.firebasestorage.app",
  messagingSenderId: "661825156843",
  appId: "1:661825156843:web:f9b59aeab0aa8d4e3530ae",
  measurementId: "G-J5R3K5Y894"
};

const firebaseApp = initializeApp(firebaseConfig);
const messaging = getMessaging(firebaseApp);

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setTokenState] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [referalInfo, setReferalInfo] = useState(null);
  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    // Загрузим sound из localStorage (можно тоже перенести в localforage, если нужно)
    return localStorage.getItem('sound') === 'enabled';
  });
  const [filters, setFilters] = useState(null);
  const [push, setPush] = useState();
  const [notifToken, setNotifToken] = useState();
  const [trackingItems, setTrackingItems] = useState([]);
  const [devices, setDevices] = useState([]);          // именно список объектов
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const tokenRef = useRef(token);
  const refreshPromiseRef = useRef(null);
  const deviceIdRef = useRef(null);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const setToken = (newToken) => {
    setTokenState(newToken);
  };
  const apiUrl = API_BASE_URL;

  const vapidKey = "BJM5hhy60YBdKW3QKdfXHue7qPFaTgAwmF2-XpB34GwsbbNRjqlxUxLy9ZOiFmlXdhurgIljxwpx5sM6i1YYKZg";

  const parseApiError = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      return data?.detail || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const normalizeDeviceId = (value) => {
    if (typeof value !== 'string') {
      return null;
    }
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }
    if (!/^[A-Za-z0-9._:-]{8,128}$/.test(normalized)) {
      return null;
    }
    return normalized;
  };

  const generateDeviceId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const randomPart = Math.random().toString(36).slice(2, 12);
    return `dev-${Date.now().toString(36)}-${randomPart}`;
  };

  const ensureDeviceId = async () => {
    if (deviceIdRef.current) {
      return deviceIdRef.current;
    }
    const stored = normalizeDeviceId(await localforage.getItem('device_id'));
    if (stored) {
      deviceIdRef.current = stored;
      return stored;
    }
    const created = generateDeviceId();
    await localforage.setItem('device_id', created);
    deviceIdRef.current = created;
    return created;
  };

  const withDeviceHeader = async (headers = {}) => {
    const nextHeaders = { ...(headers || {}) };
    const deviceId = await ensureDeviceId();
    if (deviceId) {
      nextHeaders['X-Device-Id'] = deviceId;
    }
    return nextHeaders;
  };

  const clearAuthState = async () => {
    setIsAuthenticated(false);
    setToken(null);
    setUserInfo(null);
    setReferalInfo(null);
    await localforage.removeItem('token');
  };

  const checkTokenValidity = async (checkToken) => {
    try {
      const headers = await withDeviceHeader({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${checkToken}`
      });
      const response = await fetch(`${apiUrl}/pandoras-echo`, {
        method: 'POST',
        headers,
        body: JS0N.stringify(checkToken),
      });

      if (response.ok) {
        const responseData = await response.json();
        return { status: true, data: responseData };
      }

      const detail = await parseApiError(response, 'Не удалось проверить сессию');
      if (response.status === 401 || response.status === 403) {
        return { status: false, reason: 'auth_error', httpStatus: response.status, detail };
      }
      return { status: false, reason: 'server_error', httpStatus: response.status, detail };
    } catch (error) {
      console.error('Error validating token:', error);
      return {
        status: false,
        reason: 'network_error',
        detail: error?.message || 'network_error'
      };
    }
  };

  const validateToken = async (extToken = null, options = {}) => {
    const { logoutOnAuthFail = true } = options;
    const currentToken = extToken || tokenRef.current;
    if (!currentToken) {
      return { token: null, reason: 'missing_token' };
    }

    const validation = await checkTokenValidity(currentToken);
    if (validation.status && validation.data?.status) {
      const responseData = validation.data;
      setIsAuthenticated(true);
      setToken(responseData.token);
      setUserInfo(responseData.user_info);
      await localforage.setItem('token', responseData.token);
      return { token: responseData.token, reason: 'ok' };
    }

    const reason = validation.reason || 'auth_error';
    if (reason === 'auth_error' && logoutOnAuthFail) {
      await clearAuthState();
    }

    return {
      token: null,
      reason,
      detail: validation.detail,
      httpStatus: validation.httpStatus
    };
  };

  const refreshAccessToken = async () => {
    if (refreshPromiseRef.current) {
      return await refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      return await validateToken(null, { logoutOnAuthFail: false });
    })();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  };

  const applyAuthEffects = async (responseData) => {
    setIsAuthenticated(true);
    setToken(responseData.token);
    setUserInfo(responseData.user_info);
    await localforage.setItem('token', responseData.token);

    try {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        let instructions = '';
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          instructions = "Разрешение на уведомления не предоставлено. Для включения уведомлений на iOS: перейдите в 'Настройки' → 'Приложения' → в поиске напишите 'salew' → 'Уведомления' и включите их для этого сайта.";
        } else if (/Android/i.test(navigator.userAgent)) {
          instructions = "Разрешение на уведомления не предоставлено. Для включения уведомлений на Android: откройте настройки браузера (обычно через меню с тремя точками) и разрешите уведомления для этого сайта.";
        } else {
          instructions = 'Пожалуйста, включите уведомления в настройках вашего браузера.';
        }
        alert(`Ошибка при запросе FCM токена: ${instructions}`);
        setPush(false);

        try {
          const currentToken = responseData.token;
          const headers = await withDeviceHeader({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`
          });
          const response = await fetch(`${apiUrl}/filters/update_push`, {
            method: 'PUT',
            headers
          });

          if (!response.ok) {
            throw new Error('Ошибка при обновлении фильтров');
          }
        } catch (error) {
          console.error('Ошибка при обновлении фильтров:', error);
        }
      } else {
        try {
          const swRegistration = await getFcmServiceWorkerRegistration();
          const fcmToken = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });
          if (fcmToken) {
            setNotifToken(fcmToken);

            const currentToken = responseData.token;
            const headers = await withDeviceHeader({
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${currentToken}`,
            });
            const response = await fetch(`${apiUrl}/filters/update_enpoints`, {
              method: 'PUT',
              headers,
              body: JSON.stringify({ endpoint: fcmToken }),
            });

            if (!response.ok) {
              throw new Error('Ошибка при обновлении токена уведомлений.');
            }

            const data = await response.json();
            setFilters(data.filters);
          } else {
            alert('FCM токен не получен. Попробуйте еще раз');
          }
        } catch (err) {
          console.error('Ошибка при получении или обновлении FCM-токена:', err);
          let instructions = '';
          if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            instructions = "Для включения уведомлений на iOS: перейдите в 'Настройки' → 'Приложения' → в поиске напишите 'salew' → 'Уведомления' и включите их для этого сайта.";
          } else if (/Android/i.test(navigator.userAgent)) {
            instructions = "Для включения уведомлений на Android: откройте настройки браузера (обычно через меню с тремя точками) и разрешите уведомления для этого сайта.";
          } else {
            instructions = 'Пожалуйста, включите уведомления в настройках вашего браузера.';
          }
          alert(`Ошибка при запросе FCM токена: ${err.message || err}. ${instructions}`);
        }
      }
    } catch (err) {
      alert(`Ошибка при запросе разрешения на уведомления: ${err.message || err}`);
    }
  };

  const handleAuthResponse = async (responseData, options = {}) => {
    const { showModalOnFail = true } = options;
    if (responseData && responseData.token) {
      await applyAuthEffects(responseData);
    } else if (showModalOnFail) {
      setShowModal(true);
    }
    return responseData;
  };

  const login = async (telegramData) => {
    try {
      telegramData.userAgent = navigator.userAgent;
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const serverResponse = await fetch(`${apiUrl}/check_telegram_auth`, {
        method: 'POST',
        headers,
        body: JSON.stringify(telegramData),
      });

      if (!serverResponse.ok) {
        throw new Error('Ошибка при обращении к API');
      }

      const responseData = await serverResponse.json();
      await handleAuthResponse(responseData);
      return responseData;
    } catch (error) {
      console.error('Ошибка авторизации через Telegram:', error);
      alert(`Ошибка авторизации: ${error.message}`);
      return null;
    }
  };

  const startBotHandshake = async (handshakeId, clientMeta = {}) => {
    try {
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/bot-auth/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          handshake_id: handshakeId,
          client_meta: {
            ...(clientMeta || {}),
            userAgent: navigator.userAgent,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Не удалось зарегистрировать попытку авторизации через бота');
      }
      return data;
    } catch (error) {
      console.error('Ошибка при создании записи авторизации через бота:', error);
      throw error;
    }
  };

  const checkBotHandshakeStatus = async (handshakeId) => {
    try {
      const headers = await withDeviceHeader();
      const response = await fetch(`${apiUrl}/bot-auth/status?handshake_id=${encodeURIComponent(handshakeId)}`, { headers });
      if (response.status === 404) {
        return { status: 'not_found' };
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Не удалось получить статус авторизации через бота');
      }
      return data;
    } catch (error) {
      console.error('Ошибка проверки статуса авторизации через бота:', error);
      throw error;
    }
  };

  const finalizeBotHandshake = async (handshakeId) => {
    try {
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/bot-auth/finalize`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          handshake_id: handshakeId,
          userAgent: navigator.userAgent,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Не удалось завершить авторизацию через бота');
      }
      if (data.token) {
        await handleAuthResponse(data);
      } else if (data.detail && data.detail !== 'handshake_pending') {
        await handleAuthResponse(data);
      }
      return data;
    } catch (error) {
      console.error('Ошибка завершения авторизации через бота:', error);
      throw error;
    }
  };

  const sendEmailAuthCode = async (email) => {
    try {
      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/email-auth/send-code`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Не удалось отправить код'));
      }
      return await response.json();
    } catch (error) {
      console.error('Ошибка отправки email-кода:', error);
      throw error;
    }
  };

  const verifyEmailAuthCode = async (email, code, refClaimToken = null) => {
    try {
      const body = { email, code };
      if (typeof refClaimToken === 'string' && refClaimToken.trim()) {
        body.ref_claim_token = refClaimToken.trim();
      }

      const headers = await withDeviceHeader({ 'Content-Type': 'application/json' });
      const response = await fetch(`${apiUrl}/email-auth/verify-code`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Не удалось подтвердить код'));
      }

      const responseData = await response.json();
      await handleAuthResponse(responseData, { showModalOnFail: false });
      return responseData;
    } catch (error) {
      console.error('Ошибка подтверждения email-кода:', error);
      throw error;
    }
  };

  const getProfileEmailAuth = async () => {
    const response = await fetchWithAuth(`${apiUrl}/profile/email-auth`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return await response.json();
  };

  const getProfileReferralLink = async () => {
    const response = await fetchWithAuth(`${apiUrl}/profile/referral-link`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return await response.json();
  };

  const sendProfileEmailAuthCode = async (email) => {
    const response = await fetchWithAuth(`${apiUrl}/profile/email-auth/send-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return await response.json();
  };

  const verifyProfileEmailAuthCode = async (email, code) => {
    const response = await fetchWithAuth(`${apiUrl}/profile/email-auth/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    return await response.json();
  };

  const startProfileTelegramLink = async () => {
    const response = await fetchWithAuth(`${apiUrl}/profile/telegram-link/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const getProfileTelegramLinkStatus = async (handshakeId) => {
    const response = await fetchWithAuth(
      `${apiUrl}/profile/telegram-link/status?handshake_id=${encodeURIComponent(handshakeId)}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return await response.json();
  };

  const finalizeProfileTelegramLink = async (handshakeId) => {
    const response = await fetchWithAuth(`${apiUrl}/profile/telegram-link/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handshake_id: handshakeId }),
    });
    const data = await response.json();
    if (data?.token) {
      await handleAuthResponse(data, { showModalOnFail: false });
    }
    return data;
  };

  const logout = async () => {
    await clearAuthState();
  };

  // Обёртка для запросов, которые при 401 обновляют токен
  // Возвращает response или выбрасывает ошибку
  const fetchWithAuth = async (url, options = {}, retry = true) => {
    const baseHeaders = await withDeviceHeader(options.headers || {});
    baseHeaders['Authorization'] = `Bearer ${tokenRef.current}`;
    const requestOptions = { ...options, headers: baseHeaders };

    let response = null;
    try {
      response = await fetch(url, requestOptions);
    } catch (networkError) {
      throw new Error('Ошибка сети. Проверьте интернет и попробуйте снова.');
    }

    if (response.status === 401 && retry) {
      const refreshResult = await refreshAccessToken();
      if (refreshResult?.token) {
        const retryOptions = {
          ...requestOptions,
          headers: {
            ...requestOptions.headers,
            'Authorization': `Bearer ${refreshResult.token}`
          }
        };
        try {
          response = await fetch(url, retryOptions);
        } catch (networkError) {
          throw new Error('Ошибка сети при обновлении сессии. Попробуйте снова.');
        }
      } else if (refreshResult?.reason === 'network_error' || refreshResult?.reason === 'server_error') {
        throw new Error('Временная ошибка сети/сервера при проверке сессии. Попробуйте еще раз.');
      } else {
        await clearAuthState();
        throw new Error('Сессия истекла. Авторизуйтесь снова.');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || response.statusText;
      throw new Error(errorMessage);
    }

    return response;
  };

  const getPrig = async () => {
    const response = await fetchWithAuth(`${apiUrl}/pandoras-trace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await response.json();
    setReferalInfo(data.referals);
  };

  const getWsTicket = async () => {
    const response = await fetchWithAuth(`${apiUrl}/ws-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return await response.json();
  };

  const payWithBonus = async (days) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`${apiUrl}/pandoras-pay-bonus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Ошибка при оплате бонусами:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };


  const payWithRegular = async (email, days, amount, promoCode = '') => {
    setIsLoading(true);
    try {
      const payload = { email, days, amount };
      const normalizedPromo = (promoCode || '').trim();
      if (normalizedPromo) {
        payload.promo_code = normalizedPromo;
      }
      const response = await fetchWithAuth(`${apiUrl}/pandoras-pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Ошибка при обычной оплате:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const searchSales = async (type, value) => {
    const response = await fetchWithAuth(`${apiUrl}/search-sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: type, value: value })
    });
    const data = await response.json();
    return data;
  };

  const getFilters = async () => {
    const response = await fetchWithAuth(`${apiUrl}/filters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    setFilters(data.filters);
  };

  const updateFilters = async (updatedFilters, notifToken) => {
    const sanitizedFilters = updatedFilters.map(({ id, user_id, ...rest }) => rest);
    const response = await fetchWithAuth(`${apiUrl}/filters/update`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filters: sanitizedFilters, endpoint: notifToken })
    });
    const data = await response.json();
    setFilters(data.filters);
  };

  const getLastSale = async (article) => {
    const response = await fetchWithAuth(`${apiUrl}/get_sale?article=${encodeURIComponent(article)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    return data.data_sale;
  };

  const getFeedbackSales = async ({ beforeId = null, limit = 1000 } = {}) => {
    const response = await fetchWithAuth(`${apiUrl}/feedback-sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ before_id: beforeId, limit })
    });
    const data = await response.json();
    return data.data_sale;
  };

  const toggleSound = () => {
    const newSoundState = !isSoundEnabled;
    setIsSoundEnabled(newSoundState);
    localStorage.setItem('sound', newSoundState ? 'enabled' : 'disabled');
  };


  /**
   * Создать новую запись для отслеживания
   * @param {Object} trackingData – поля модели TrackingList (id не нужен при создании)
   */
  const createTrackingItem = async (trackingData) => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackingData),
      });
      const data = await response.json();
      // Если хочешь, можешь тут же подливать в state trackingItems
      if (data.status) {
        // Объединяем новые данные со старыми
        setTrackingItems((prev) => [...prev, data.data]);
      }
      return data;
    } catch (error) {
      console.error('Ошибка createTrackingItem:', error);
      throw error;
    }
  };

  /**
   * Обновить запись для отслеживания
   * @param {Object} trackingData – поля модели TrackingList (обязательно id)
   */
  const updateTrackingItem = async (trackingData) => {
    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackingData),
      });
      const data = await response.json();
      if (data.status && data.data) {
        // Обновляем в локальном стейте
        setTrackingItems((prev) => {
          return prev.map((item) => (item.id === data.data.id ? data.data : item));
        });
      }
      return data;
    } catch (error) {
      console.error('Ошибка updateTrackingItem:', error);
      throw error;
    }
  };

  /**
   * Получить все отслеживаемые товары для пользователя
   */
  const getTrackingList = async () => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/get_all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.status && data.data) {
        setTrackingItems(data.data);
      }
      return data;
    } catch (error) {
      console.error('Ошибка getTrackingList:', error);
      throw error;
    }
  };

  /**
   * Удалить запись из отслеживания
   * @param {number} itemId – ID записи, которую хотим удалить
   */
  const deleteTrackingItem = async (itemId) => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId }),
      });
      const data = await response.json();
      if (data.status) {
        // Удаляем из локального стейта
        setTrackingItems((prev) => prev.filter((item) => item.id !== itemId));
      }
      return data;
    } catch (error) {
      console.error('Ошибка deleteTrackingItem:', error);
      throw error;
    }
  };

  /**
   * Запросить дополнительную инфу (историю цены и т.п.) 
   * @param {number} article – артикул товара
   */
  const parseTrackingItem = async (article) => {

    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ article }),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Ошибка parseTrackingItem:', error);
      throw error;
    }
  };


  /**
 * Обновить поле pause для записи отслеживания
 * @param {number} itemId – ID записи, которую нужно обновить
 * @param {boolean} pause – новое значение для поля pause
 */
  const updateTrackingItemPause = async (itemId, pause) => {
    try {
      const response = await fetchWithAuth(`${apiUrl}/tracking_list/update_pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, pause }),
      });
      const data = await response.json();
      if (data.status) {
        // Обновляем локальное состояние, заменяя соответствующую запись
        setTrackingItems((prev) =>
          prev.map((item) =>
            item.id === itemId ? { ...item, pause } : item
          )
        );
      }
      return data;
    } catch (error) {
      console.error('Ошибка updateTrackingItemPause:', error);
      throw error;
    }
  };
  /* ---------- DEVICES ---------- */
  const getDevices = async () => {
    try {
      const resp = await fetchWithAuth(`${apiUrl}/devices`);
      const data = await resp.json();
      if (data.status) {
        setDevices(data.devices_info[0]);              // <– только список
        setCurrentDeviceId(data.devices_info[1]);   // <– отдельный id
      }
    } catch (err) {
      console.error('Ошибка при получении устройств:', err);
    }
  };

  const deleteDevice = async (deviceId) => {
    try {
      await fetchWithAuth(`${apiUrl}/devices/${deviceId}`, { method: 'DELETE' });
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch (err) {
      alert(err.message || 'Не удалось удалить устройство');
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await ensureDeviceId();
        const storedToken = await localforage.getItem('token');
        if (storedToken) {
          const validation = await validateToken(storedToken, { logoutOnAuthFail: false });
          const newToken = validation?.token;
          if (!newToken) {
            if (validation?.reason === 'auth_error') {
              await clearAuthState();
            }
          } else {
            // Если токен валиден, проверим FCM-токен
            try {
              const swRegistration = await getFcmServiceWorkerRegistration();
              const fcmToken = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });
              // Если получили FCM-токен и он отличается от сохраненного
              if (fcmToken && fcmToken !== notifToken) {
                const headers = await withDeviceHeader({
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${newToken}`,
                });
                const response = await fetch(`${apiUrl}/filters/update_enpoints`, {
                  method: 'PUT',
                  headers,
                  body: JSON.stringify({ endpoint: fcmToken }),
                });

                if (response.ok) {
                  const data = await response.json();
                  setFilters(data.filters);
                  setNotifToken(fcmToken);
                } else {
                  alert('Ошибка при обновлении FCM-токена на сервере');
                }
              }
            } catch (err) {
              console.error('Ошибка при получении или обновлении FCM-токена при инициализации:', err);
              // Не критично, просто не обновляем пуш-токен
            }
          }
        }
      } catch (error) {
        alert('Ошибка при инициализации:', error, 'перезагрузите приложение и если не поможет напишите @salew_support');
        // В случае ошибки мы ничего не делаем, просто продолжим
      } finally {
        // Гарантированно снимаем isLoading
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider value={{
      isLoading,
      setIsLoading,
      isAuthenticated,
      token,
      userInfo,
      login,
      startBotHandshake,
      checkBotHandshakeStatus,
      finalizeBotHandshake,
      sendEmailAuthCode,
      verifyEmailAuthCode,
      getProfileEmailAuth,
      getProfileReferralLink,
      sendProfileEmailAuthCode,
      verifyProfileEmailAuthCode,
      startProfileTelegramLink,
      getProfileTelegramLinkStatus,
      finalizeProfileTelegramLink,
      logout,
      showModal,
      setShowModal,
      getPrig,
      getWsTicket,
      referalInfo,
      payWithBonus,
      payWithRegular,
      isSoundEnabled,
      toggleSound,
      searchSales,
      getFilters,
      updateFilters,
      filters,
      getFeedbackSales,
      getLastSale,
      trackingItems,
      createTrackingItem,
      updateTrackingItem,
      getTrackingList,
      deleteTrackingItem,
      parseTrackingItem,
      updateTrackingItemPause,
      devices,
      getDevices,
      deleteDevice,
      currentDeviceId
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
